import React from 'react';
import CompatioApi from '../../api/CompatioApi';
import EcommerceApi from '../../api/EcommerceApi';
import ReactResizeDetector from 'react-resize-detector';
import { FormattedMessage } from 'react-intl';
import _, { get as _get } from 'lodash-es';
import ProductList from '../product-list/ProductList';
import CompatibleProduct from './CompatibleProduct';
import CompatioAnalytics from '../../api/CompatioAnalytics';
import PopupXRS from '../popups/PopupXRS';
import ProductSort from '../utils/ProductSort';
import compatioImg from '../../styles/images/build-it-out.png';
import PopupBundler from '../popups/PopupBundler';

class CompatibleProducts extends React.Component {
  /**
   * Track whether component is mounted
   *
   * @type {boolean}
   * @private
   */
  _isMounted = false;

  anchor = document.getElementById('xrsanchor');

  static defaultProps = {
    /**
     * The merchant product ID. This value should always be passed in during the initialization of this component
     */
    productSku: null,
    /**
     * The product name of the current product being viewed.
     */
    productName: null,
    /**
     * Products to show user until "Show More" button hides the rest of the products
     */
    productsPerPage: 10,
    /**
     * Breakpoint to switch between tabs/accordion behavior
     */
    desktopModeWidth: 768,
    /**
     * For variable products, if user hasn't selected a specific product, a random child SKU will be passed to this
     * component so that a list of categories can be retrieved from the Compatio API. If a random SKU is passed, this
     * prop should be set to "true"
     */
    isProductSkuRandom: false,
    /**
     * If enabled, when page first loads, all product data will be retrieved from ecommerce API (for the
     * first "productsPerPage" products within each category). This will result in compatible products showing
     * immediately when a category is clicked. However it will result in more bandwidth usage for users and a
     * higher load on the ecommerce server API.
     */
    isPreloadEcommerceDataEnabled: true,
  };

  constructor(props) {
    super(props);
    const merchantProductSku = props.productSku;
    const merchantProductName = props.productName;
    if (
      _get(
        window,
        'compatioConfig.compatibleProducts.callbacks.beforeConstruct'
      )
    ) {
      window.compatioConfig.compatibleProducts.callbacks.beforeConstruct();
    }

    window.compatioConfig.compatibleProducts.methods =
      window.compatioConfig.compatibleProducts.methods || {};

    // Register function in global namespace
    window.compatioConfig.compatibleProducts.methods.handleChangeProductSku = this.handleChangeProductSku.bind(
      this
    );

    this.state = {
      compatioCategories: [],
      activeCompatioCategories: {},
      defaultSorting: 'compatioscore_descending',
      merchantProductSku: merchantProductSku,
      merchantProductName: merchantProductName,
      isProductSkuRandom: this.props.isProductSkuRandom,
      isDesktopMode: this.isDesktopMode(),
      isBundlerOpen: false,
      IsPlatformShopify:
        typeof _get(window, 'compatioConfig.shopify') === 'object',
      errorMessage: false,
      maximumProducts: _get(window, 'compatioConfig.shopify.maximumProducts'),
      maximumCategories: _get(
        window,
        'compatioConfig.shopify.maximumCategories'
      ),
    };

    this.setMerchantProductSku(merchantProductSku);
    this.onResize = this.onResize.bind(this);
    this.handleChangeSortOrder = this.handleChangeSortOrder.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  toggleBundler = () => {
    const { isBundlerOpen } = this.state;
    this.setState({
      isBundlerOpen: !isBundlerOpen,
    });
  };

  /**
   * Update sort order
   *
   * @param sortOrder
   */
  handleChangeSortOrder(sortOrder) {
    this.setState({
      defaultSorting: sortOrder,
    });

    // this.props.changeSorting(sortOrder);
  }

  /**
   * Allow a third-party script to update the current merchant product ID
   *
   * Ecommerce platform will call this when a user changes the child SKU on a Variable Product page
   *
   * @param productSku
   * @param isProductSkuRandom
   */
  handleChangeProductSku(productSku, isProductSkuRandom) {
    // alert(productSku);
    // If isProductSkuRandom is undefined, coerce to false
    isProductSkuRandom = !!isProductSkuRandom;

    // Don't update anything if nothing has changed
    if (
      productSku === this.state.merchantProductSku &&
      isProductSkuRandom === this.state.isProductSkuRandom
    ) {
      return;
    }
    this.setState({ isProductSkuRandom: isProductSkuRandom });
    this.setMerchantProductSku(productSku);
  }

  /**
   * Update this component to show compatibility information for a different product ID
   *
   * @param merchantProductSku
   * @returns {Promise<void>}
   */
  async setMerchantProductSku(merchantProductSku) {
    const eCommercePlatform = this.state.IsPlatformShopify
      ? 'shopify'
      : 'magento';
    try {
      const compatioCategoryData = await CompatioApi.getCategoryDataByProductSku(
        merchantProductSku,
        this.state.maximumCategories,
        this.state.maximumProducts,
        eCommercePlatform
      );
      if (compatioCategoryData.length > 0) {
        this.anchor.style.display = 'block';
        if (this._isMounted) {
          this.setState({
            merchantProductSku: merchantProductSku,
            compatioCategories: compatioCategoryData,
            errorMessage: false,
          });
        }

        if (!this.props.isPreloadEcommerceDataEnabled) {
          return;
        }

        const allMerchantProductSkus = CompatioApi.extractAllProductSkus(
          compatioCategoryData
        );
        try {
          // Preload ecommerce data; don't do anything with the returned data
          await EcommerceApi.loadProductDataFromEcommerceXrs(
            allMerchantProductSkus,
            eCommercePlatform,
            compatioCategoryData
          );
          // console.log(allMerchantProductSkus,'allMerchantProductSkus')
        } catch (e) {
          // Don't do anything with errors thrown by loadProductDataFromEcommerce (such as an API timeout), since
          // subsequent calls to this method could be successful (since it will be a smaller set of data requested)
          console.error(e);
        }
      } else {
        this.anchor.style.display = 'none';
        this.setState({
          errorMessage: true,
        });
      }
    } catch (e) {
      this.setState({
        errorMessage: true,
      });
      //console.error(e);
    }
  }

  /**
   * Determine if category is currently active
   *
   * @param compatioCategoryData
   * @param categoryId
   * @returns {*}
   */
  isCategoryIdInActiveCategories(compatioCategoryData, categoryId) {
    return compatioCategoryData.find(categoryObject => {
      return categoryObject.compatioCategoryId === categoryId;
    });
  }

  /**
   * Toggle mode, if resize has resulted in mode changing
   */
  onResize() {
    const isDesktopMode = this.isDesktopMode();
    // Only update if the mode has changed
    if (this.state.isDesktopMode !== isDesktopMode) {
      this.setState({ isDesktopMode: isDesktopMode });

      // If changing from mobile > desktop, close all panels so that multiple tabs aren't selected at once
      if (isDesktopMode) {
        this.setState({
          activeCompatioCategories: {},
        });
      }
    }
  }

  // TODO: Implement accessibility for accordion/tab view. Reference: https://getbootstrap.com/docs/4.1/components/collapse/#accessibility
  /**
   * Toggle the visibility of "compatible products" for a given category
   *
   * @param compatioCategoryId
   */
  setCompatioCategoryId(compatioCategoryId) {
    let activeCategories = {};
    // Allow multiple sections to be open at a time on mobile
    if (!this.isDesktopMode()) {
      activeCategories = { ...this.state.activeCompatioCategories };
    }

    let analyticsAction;
    if (
      this.state.activeCompatioCategories.hasOwnProperty(compatioCategoryId)
    ) {
      // If category section is expanded, collapse it
      delete activeCategories[compatioCategoryId];
      analyticsAction = 'close';
    } else {
      // Show category section
      activeCategories[compatioCategoryId] = true;
      analyticsAction = 'open';
    }

    CompatioAnalytics.track(CompatioAnalytics.events.xrsCategoryClick, {
      variant_id: this.state.merchantProductSku,
      product_name: this.state.merchantProductName,
      category_id: compatioCategoryId,
      category_position: this.getCategoryPosition(
        this.state.compatioCategories,
        compatioCategoryId
      ),
      category_name: this.getCategoryName(
        this.state.compatioCategories,
        compatioCategoryId
      ),
      click_action: CompatioAnalytics.clicks.xrsOpenCategory,
    });

    this.setState({
      activeCompatioCategories: activeCategories,
    });
  }

  /**
   * Get position of current category vs all categories. Used for analytics purposes.
   *
   * @param allCategories
   * @param currentCategoryId
   * @returns {number}
   */
  getCategoryPosition(allCategories, currentCategoryId) {
    let position = allCategories.findIndex(
      categoryData => categoryData.compatioCategoryId === currentCategoryId
    );
    return ++position;
  }

  /**
   * Get name of current category. Used for analytics purposes.
   *
   * @param allCategories
   * @param currentCategoryId
   * @returns {name}
   */
  getCategoryName(allCategories, currentCategoryId) {
    let CurrentCategoryName = allCategories.find(
      categoryData => categoryData.compatioCategoryId === currentCategoryId
    ).categoryName;
    return CurrentCategoryName;
  }

  /**
   * Get whether viewport is currently desktop mode
   *
   * @returns {boolean}
   */
  isDesktopMode() {
    return window.innerWidth > this.props.desktopModeWidth;
  }

  renderCategoriesAndProducts() {
    const compatioCategories = this.state.compatioCategories;
    // const sortedCategories = compatioCategories.sort((a, b) => {
    //   return a.relevanceRank - b.relevanceRank;
    // });
    const categories = [];
    const productLists = [];

    //sortedCategories.forEach((category, index) => {
    compatioCategories.forEach((category, index) => {
      let categoryIsActive = this.state.activeCompatioCategories.hasOwnProperty(
        category.compatioCategoryId
      );
      categories.push(
        <CompatibleProduct
          key={category.compatioCategoryId}
          isActive={categoryIsActive}
          onClick={() =>
            this.setCompatioCategoryId(category.compatioCategoryId)
          }
          {...category}
        />
      );

      // Don't add product list if the associated category is not selected
      if (!categoryIsActive) {
        return;
      }

      let productList;
      if (this.state.isProductSkuRandom) {
        // For Variable Products, if specific child SKU has not been selected, show placeholder message
        productList = (
          <h3
            key={`selectProductOption_${category.compatioCategoryId}_${this.state.merchantProductSku}`}
          >
            <FormattedMessage
              id="CompatibleProducts.variable_product_select_options"
              defaultMessage="Please select product options above to see compatible products"
            />
          </h3>
        );
      } else {
        productList = (
          <ProductList
            key={`productList_${category.compatioCategoryId}_${this.state.merchantProductSku}`}
            categoryId={category.compatioCategoryId}
            categoryPosition={index}
            categoryName={category.categoryName}
            compatioSorting={_get(category, 'sorting', false)}
            defaultSorting={this.state.defaultSorting}
            merchantProductSku={this.state.merchantProductSku}
            productName={this.props.productName}
            productsPerPage={this.props.productsPerPage}
            maximumCategories={this.state.maximumCategories}
            maximumProducts={this.state.maximumProducts}
          />
        );
      }

      if (!this.isDesktopMode()) {
        // On mobile, product lists need to be immediately adjacent to their corresponding category links
        categories.push(productList);
      } else {
        productLists.push(productList);
      }
    });

    return [categories, productLists];
  }

  render() {
    // TODO: Figure out why render is called twice when clicking a category thumbnail
    // console.trace('CompatibleProducts.render. Current products: ', this.state.currentProducts);

    const compatioCategories = _.cloneDeep(this.state.compatioCategories);
    const merchantProductName = this.state.merchantProductName;
    if (this.state.errorMessage !== false) return null;
    if (!compatioCategories.length) {
      //CompatioApi.reset();
      return null;
    }

    const [categories, productLists] = this.renderCategoriesAndProducts();

    return (
      <>
        <div id="xrs-anchor"></div>
        <div className="xrs-container">
          <div className="xrs-compatible-products">
            <div className="xrs-heading">
              <h3>
                <img src={compatioImg} className="compatio-img"></img>
                <FormattedMessage
                  id="CompatibleProducts.heading"
                  defaultMessage="Compatible Products."
                />
              </h3>
              <div className="xrs-logo">
                <a href="https://www.compat.io/feedback" target="_blank">
                  <img
                    src="https://d1qrqn8mibon78.cloudfront.net/powered-by-compatio.png"
                    alt="Compatio"
                    style={{ textAlign: 'right' }}
                  />
                </a>
              </div>
            </div>
            <div className="xrs-description">
              <span id="xrs-text"></span>
              <PopupXRS />
            </div>
            <div className="xrs-categories">
              {categories}
              <div className="xrs-sort">
                <ProductSort
                  handleChangeSortOrder={this.handleChangeSortOrder}
                  currentSortOrder={this.state.defaultCategorySorting}
                  sorting={this.state.sorting}
                />
              </div>
            </div>
            {productLists}

            <ReactResizeDetector
              handleWidth
              refreshMode="debounce"
              refreshRate={250}
              onResize={this.onResize}
            />
          </div>
        </div>
        <button
          id="xrs-bundle"
          className="button"
          onClick={this.toggleBundler}
          style={{ visibility: 'hidden' }}
        >
          {' '}
          Open Bundler{' '}
        </button>
        {this.state.isBundlerOpen ? (
          <PopupBundler
            isOpen={this.state.isBundlerOpen}
            bundlerType="bundler"
            toggleBundler={this.toggleBundler}
            productData={[...compatioCategories]}
            parentProduct={this.props}
            parentProductSku={this.state.merchantProductSku}
            defaultBacking={
              this.props.backingData &&
              this.props.backingData[0] &&
              this.props.backingData[0].variantId
            }
          />
        ) : null}
      </>
    );
  }
}

export default CompatibleProducts;
